package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.TimeRange
import kotlinx.datetime.LocalDate

/*
 * Automatic scheduling (SH-44): the models the solver reads and the plan it returns.
 * The algorithm itself is described on [AutoScheduler].
 */

/** The legal floor of daily rest in Poland (Kodeks pracy art. 132): never planned below this ([LabourCode]). */
const val LEGAL_MINIMUM_REST_HOURS = 11

/** The days to plan, both inclusive (e.g. 16–24 of the month). */
data class ScheduleRange(val start: LocalDate, val endInclusive: LocalDate) {
    init {
        require(start <= endInclusive) { "The range must not end before it starts" }
    }

    operator fun contains(isoDate: String): Boolean = isoDate >= start.toString() && isoDate <= endInclusive.toString()
}

// --- Input ----------------------------------------------------------------------------------------

/**
 * Everything the solver needs, already read from the repositories. Pure data: the same problem
 * always gives the same plan (see [ScheduleOptions.seed]).
 */
data class ScheduleProblem(
    val rosterId: String,
    /** The roster's month: targets and "hours this month" are counted for it. */
    val year: Int,
    val month: Int,
    val range: ScheduleRange,
    /** All shifts of the roster (the month). Unassigned ones inside [range] are filled. */
    val shifts: List<Shift>,
    val staff: List<StaffProfile>,
    /**
     * Shifts the people have outside this roster: the last days of the previous month, the first
     * of the next one, and other workplaces (multi-engagement). They block overlaps and count for
     * rest and consecutive days, but never for this roster's hour targets.
     */
    val contextShifts: List<ContextShift> = emptyList(),
    /** The workplace's `definedAvailabilitySlots`, to read [DayPreference.preferredSlotId]. */
    val availabilitySlots: List<AvailabilitySlot> = emptyList(),
    /**
     * Full-time hours of the month (PayrollRules.workingHours). Used to compare people whose
     * target is unknown with the rest.
     */
    val nominalMonthlyHours: Double = 160.0,
    val options: ScheduleOptions = ScheduleOptions(),
    /**
     * SH-90: assignments suggested from outside the rules (the AI step), already mapped back from
     * aliases. Each is checked against every hard rule before anything else is planned; the ones
     * that break a rule are dropped ([SchedulePlan.dropped]) and the rules fill what is left.
     */
    val proposals: List<ProposedAssignment> = emptyList()
)

/** SH-90: an outside suggestion for one open shift (see [ScheduleProblem.proposals]). */
data class ProposedAssignment(
    val shiftId: String,
    val userId: String,
    /** Why it was suggested; shown next to the row (null: [AssignmentReason.BEST_FIT]). */
    val reason: AssignmentReason? = null
)

/** A shift of one of the people that is not part of the roster being planned. */
data class ContextShift(
    val userId: String,
    val date: String,
    val plannedTime: TimeRange,
    /** The same workplace (another month): counts for weekly limits; else another workplace. */
    val atThisWorkplace: Boolean
)

/**
 * One person as the solver sees them. Built by [StaffProfiles.build] from what the app stores
 * today; who leads comes from the workplace's ShiftLeadRule (SH-104), supervision from the
 * engagement (SH-47), [constraints] are the planner's.
 */
data class StaffProfile(
    val userId: String,
    val displayName: String,
    val role: String?,
    val skills: Set<String> = emptySet(),
    /** The workplace owner (rule 6: gets the morning shifts). Counts as a shift lead. */
    val isOwner: Boolean = false,
    /** Can lead a shift (rule 5): the owner, a manager, or a lead position or skill (ShiftLeadRule). */
    val leadsShifts: Boolean = false,
    /** Must never be at work with only other people who need supervision (rule 7). */
    val needsSupervision: Boolean = false,
    val contract: SystemContractType = SystemContractType.NOT_SET,
    val target: HoursTarget = HoursTarget.unknown(TargetSource.NOT_READ),
    /** Day preferences by ISO date. */
    val availability: Map<String, DayPreference> = emptyMap(),
    /** Whether the person submitted availability for the month at all. */
    val availabilitySubmitted: Boolean = availability.isNotEmpty(),
    /** ISO dates of approved absences (sick leave, vacation...). */
    val absentDates: Set<String> = emptySet(),
    /**
     * SH-47: the last ISO day their medical exam and OHS training both cover; later days are
     * never planned (the auto-assign never forces past it, whatever the workplace's setting).
     * Null: nothing known to run out. [StaffProfiles.checksValidThrough] builds it.
     */
    val staffChecksValidThrough: String? = null,
    /**
     * Hard personal rules (rule 9). The solver cannot read free text: a manager or an AI step turns
     * the person's notes into these.
     */
    val constraints: List<StaffConstraint> = emptyList(),
    /** Free text the person wrote (availability comments), kept for that later step and the UI. */
    val notes: List<String> = emptyList()
)

/** How many hours a month the person should work here. */
data class HoursTarget(val hours: Double?, val source: TargetSource) {
    val isKnown: Boolean get() = hours != null

    companion object {
        fun of(hours: Double, source: TargetSource) = HoursTarget(hours, source)
        fun unknown(source: TargetSource) = HoursTarget(null, source)
    }
}

enum class TargetSource {
    /** FinancialEngagement.declaredNumberOfHours. */
    DECLARED_HOURS,
    /** Employment contract with a monthly salary: FTE × the month's nominal working hours. */
    FULL_TIME_SHARE,
    /** Pay terms exist, but no hours were declared. */
    NOT_DECLARED,
    /** No pay terms for this workplace (e.g. an unpaid owner). */
    NO_PAY_TERMS,
    /** The planner may not read pay terms (not a financial manager). */
    PAY_TERMS_UNREADABLE,
    /** Pay terms were not requested. */
    NOT_READ,
    /** Set by hand (tests, a future override). */
    MANUAL
}

/**
 * A personal hard rule (rule 9: "follow each employee's notes strictly"). Every one is checked
 * like availability: a shift that breaks it is never given to the person.
 * Times are "HH:mm" clock times of the shift's own day.
 */
sealed interface StaffConstraint {
    /** Where the rule came from (the note), for the explanation in the UI. */
    val note: String?

    /** At most [days] working days in a week (the workplace's first day of week). */
    data class MaxDaysPerWeek(val days: Int, override val note: String? = null) : StaffConstraint

    /** At most [hours] in a week at this workplace. */
    data class MaxHoursPerWeek(val hours: Double, override val note: String? = null) : StaffConstraint

    /** At most [hours] this month at this workplace (stricter than the target). */
    data class MaxHoursPerMonth(val hours: Double, override val note: String? = null) : StaffConstraint

    /** At most [days] working days this month at this workplace. */
    data class MaxDaysPerMonth(val days: Int, override val note: String? = null) : StaffConstraint

    /** A personal limit of days in a row, below the workplace's. */
    data class MaxConsecutiveDays(val days: Int, override val note: String? = null) : StaffConstraint

    /** A personal rest longer than the workplace's (never shorter: the legal floor stays). */
    data class MinRestHours(val hours: Int, override val note: String? = null) : StaffConstraint

    /** Only shifts starting at or after [earliest] and before [latestExclusive] ("only mornings"). */
    data class StartBetween(val earliest: String, val latestExclusive: String, override val note: String? = null) : StaffConstraint

    /** Only shifts that end by [latestEnd] on their own day (no overnight shifts). */
    data class EndBy(val latestEnd: String, override val note: String? = null) : StaffConstraint

    /** Never on these ISO days of week (1 = Monday ... 7 = Sunday). */
    data class NotOnDaysOfWeek(val isoDays: Set<Int>, override val note: String? = null) : StaffConstraint

    /** Only on these ISO days of week. */
    data class OnlyOnDaysOfWeek(val isoDays: Set<Int>, override val note: String? = null) : StaffConstraint

    /** Never on these ISO dates. */
    data class NotOnDates(val dates: Set<String>, override val note: String? = null) : StaffConstraint

    companion object {
        /** Their "Rano": a shift starting before 9:00. */
        fun onlyMornings(note: String? = null) = StartBetween("00:00", "09:00", note)
        /** Their "Popołudnie": a shift starting at 12:00 or later. */
        fun onlyAfternoons(note: String? = null) = StartBetween("12:00", "24:00", note)
    }
}

// --- Options ------------------------------------------------------------------------------------

/** How a day's preferred slot is used. */
enum class PreferenceMode {
    /** The preferred slot is the only acceptable time that day (their sheet: "Rano" = mornings only). */
    STRICT,
    /** A shift in the preferred slot is favoured; others are still possible. */
    SOFT,
    /** Preferred slots are ignored. */
    IGNORE
}

/**
 * Decides whether a shift fits an availability slot. Minutes are from the start of the shift's
 * own day; the end may exceed 24 h for an overnight shift.
 */
fun interface SlotMatcher {
    fun matches(shiftStartMinute: Int, shiftEndMinute: Int, slot: AvailabilitySlot): Boolean

    companion object {
        /**
         * The default: the shift starts within the slot (start <= shift start < end). This is how
         * their sheet read "Rano" (start before 9:00), "Międzyzmiana" (9:00–12:00) and
         * "Popołudnie" (from 12:00).
         */
        val StartsWithinSlot = SlotMatcher { start, _, slot ->
            val from = clockMinutes(slot.startTime)
            val to = clockMinutes(slot.endTime)
            if (from < to) start in from until to else start >= from || start < to
        }

        /** Stricter: the whole shift lies inside the slot. */
        val WithinSlot = SlotMatcher { start, end, slot ->
            val from = clockMinutes(slot.startTime)
            var to = clockMinutes(slot.endTime)
            if (to <= from) to += 24 * 60
            start >= from && end <= to
        }
    }
}

/**
 * How strongly each soft rule pulls when choosing between people who all satisfy the hard rules.
 * The score of a candidate is the sum of the terms that apply (see [AutoScheduler]).
 */
data class ScheduleWeights(
    /** Rule 5: a shift lead for a block that has none yet. */
    val shiftLeadNeeded: Double = 60.0,
    /** Rule 5: someone who is not a lead for a block that has only leads. */
    val nonLeadNeeded: Double = 40.0,
    /** Keeps leads for blocks that need them: a second lead in a block costs this. */
    val spareLead: Double = 15.0,
    /** Rule 6: the owner on a morning shift (and minus half of it on a later one). */
    val ownerMorning: Double = 50.0,
    /** Rule 2: the scarcest, most committed person gets all of it, the most flexible none. */
    val scarcity: Double = 30.0,
    /** Hours below target: nobody at 0 % gets all of it, 150 % and above none. */
    val belowTarget: Double = 40.0,
    /** Rule 4: the day joins a run of worked days that stays within the preferred length. */
    val extendsRun: Double = 20.0,
    /** Rule 4: per day a run grows beyond the preferred length. */
    val overlongRun: Double = 20.0,
    /** Rule 3: the person has been off for longer than [ScheduleOptions.maxIdleDays]. */
    val longBreak: Double = 15.0,
    /** A shift in the preferred slot of the day. */
    val preferredSlot: Double = 10.0,
    /** Scale of the seeded random tie-break (keeps equal candidates from always going one way). */
    val tieBreak: Double = 0.5
)

/**
 * The owner's rules as settings. The defaults follow their prompt; the legal floors stay
 * whatever is configured (rest never below [LEGAL_MINIMUM_REST_HOURS]).
 */
data class ScheduleOptions(
    /** Replan the range: assignments inside it are dropped and planned again (clocked shifts stay). */
    val replan: Boolean = false,
    /** Rule 1: a day without submitted availability means "not available". */
    val requireSubmittedAvailability: Boolean = true,
    /**
     * SH-221: the workplace's first roster (none published or locked yet). People who sent no
     * availability for the month count as available on every day, so a new owner gets a full
     * proposal; approved absences, shifts elsewhere and every other rule still apply, and people
     * who did answer keep their answers. From the second month on rule 1 applies unchanged.
     */
    val firstRoster: Boolean = false,
    val preferenceMode: PreferenceMode = PreferenceMode.STRICT,
    val slotMatcher: SlotMatcher = SlotMatcher.StartsWithinSlot,
    /** Rule 8: rest between the end of one shift and the start of the next. */
    val minRestHours: Int = 15,
    /** Rule 3: nobody 7 days in a row (also the weekly rest of the labour code). */
    val maxConsecutiveDays: Int = 6,
    /** Rule 4: runs of worked days of this length are preferred. */
    val preferredRunDays: IntRange = 2..5,
    /** Rule 3 (soft): after this many days off, the person is favoured. */
    val maxIdleDays: Int = 4,
    /** Shifts per person per day (their balancing never gave anyone two shifts on one day). */
    val maxShiftsPerDay: Int = 1,
    /** Rule 5: every block of two or more shifts has a shift lead... */
    val requireShiftLead: Boolean = true,
    /** ...and someone who is not a lead. */
    val requireNonLead: Boolean = true,
    /**
     * Two shifts of a day are in one block (rule 5) when they overlap by at least this share of
     * the shorter one; blocks are joined transitively. 0.5 keeps a short handover (a 6:45–14:45
     * shift and a 13:30–21:30 one) from merging the morning and evening crews; 0.0 joins any
     * overlap.
     */
    val blockMinOverlap: Double = 0.5,
    /**
     * SH-133: a day where people work has a shift lead, also when its shifts are not one block
     * (the roster's "no shift lead" day warning, [com.evoapps.evoshift.domain.model.DayStaffing]).
     * Set with [requireShiftLead] where the workplace marks lead positions or skills. A day no lead
     * can work at all is still filled, and reported ([WarningKind.DAYS_WITHOUT_LEAD]).
     */
    val requireLeadEachDay: Boolean = false,
    /**
     * SH-133: the workplace applies the 11-hour and 35-hour rest to every contract (its own
     * policy; on by default, the owner's rule 8). Off: only employees get them (the labour code),
     * and everyone else only [minRestHours] between shifts. Never for the owner or a partner.
     */
    val restRulesForAllContracts: Boolean = true,
    /** Rule 6. */
    val ownerTakesMornings: Boolean = true,
    /** Rule 6: a shift starting before this is a morning shift. */
    val morningEndsAt: String = "12:00",
    /**
     * Hours a person may be planned above their monthly target. 0 = the target is a cap (their
     * declared hours are what they asked for).
     */
    val maxHoursOverTarget: Double = 0.0,
    /** The balancing pass (their optymalizujGrafikAlgorytmem), civil-law contracts only. */
    val balanceCivilLaw: Boolean = true,
    /** Balancing stops when every hours/target ratio is within this of the others. */
    val balanceTolerance: Double = 0.1,
    val balanceMaxIterations: Int = 100,
    /** ISO day the week starts on, for weekly limits (WorkplaceSettings.firstDayOfWeek). */
    val firstDayOfWeek: Int = 1,
    /** Tie-break seed: the same seed gives the same plan; another seed may break ties differently. */
    val seed: Long = 0,
    val weights: ScheduleWeights = ScheduleWeights()
) {
    /** The rest actually planned: never below the legal floor. */
    val effectiveRestHours: Int get() = maxOf(LEGAL_MINIMUM_REST_HOURS, minRestHours)

    /** SH-221: [person] sent no availability and this is the first roster, so every day counts as available. */
    fun assumesAvailable(person: StaffProfile): Boolean = firstRoster && !person.availabilitySubmitted

    /** SH-133: the rest planned for [contract]: the legal floor only where the rest rules apply. */
    fun restHoursFor(contract: com.evoapps.evoshift.domain.model.SystemContractType): Int =
        if (LabourCode.restRulesApply(contract, restRulesForAllContracts)) effectiveRestHours else minRestHours
}

// --- Output ---------------------------------------------------------------------------------------

/** Why someone cannot take a shift (or why a shift stays empty). Ordered from the most basic. */
enum class ScheduleReason {
    /** Nobody is on the team. */
    NO_STAFF,
    /** The shift asks for a role or skills the person does not have. */
    NOT_QUALIFIED,
    /** Target of 0 hours this month: not scheduled at all. */
    NO_TARGET_HOURS,
    /** An approved absence (sick leave, vacation) that day. */
    ABSENT,
    /** SH-47: their occupational medical exam or OHS training has run out by that day. */
    STAFF_CHECK_EXPIRED,
    /** Rule 1: marked "not available" (their "ND"). */
    UNAVAILABLE,
    /** Rule 1: gave no availability for that day. */
    NO_AVAILABILITY,
    /** The shift is outside the slot they asked for that day. */
    OUTSIDE_PREFERRED_SLOT,
    /** Rule 9: one of their personal rules. */
    PERSONAL_CONSTRAINT,
    /** Overlaps another of their shifts here. */
    OVERLAPS_OWN_SHIFT,
    /** Overlaps their shift at another workplace. */
    BUSY_ELSEWHERE,
    /** Already works that day. */
    ALREADY_WORKING_THAT_DAY,
    /** Rule 8: too little rest before or after another shift. */
    INSUFFICIENT_REST,
    /** Rule 3: would work too many days in a row. */
    TOO_MANY_CONSECUTIVE_DAYS,
    /** SH-112: the week would have less than 35 hours of uninterrupted rest (labour code art. 133). */
    NO_WEEKLY_REST,
    /**
     * SH-112, employment contracts: the day above the daily norm, the month above 48 hours a week
     * on average, or its overtime above the yearly cap's monthly share (labour code art. 129–151).
     */
    LABOUR_HOURS_LIMIT,
    /** Their monthly hours target is reached. */
    TARGET_REACHED,
    /** Rule 5: the block would have no shift lead. */
    NEEDS_SHIFT_LEAD,
    /** Rule 5: the block would have only shift leads. */
    NEEDS_NON_LEAD,
    /** Rule 7: they would be at work with nobody who can supervise them. */
    NEEDS_SUPERVISION
}

/** Why a person was chosen, for the explanation next to the assignment. The first is the step. */
enum class AssignmentReason {
    /** Rule 6: the owner's morning shift. */
    OWNER_MORNING,
    /** Rule 5: the block needed a shift lead. */
    SHIFT_LEAD_NEEDED,
    /** Rule 5: the block needed someone who is not a lead. */
    NON_LEAD_NEEDED,
    /** The best of the people who could take it. */
    BEST_FIT,
    /** The balancing pass moved it here to even out hours. */
    BALANCED_HOURS,
    /** Rule 2: gave little availability, so their few days are used first. */
    SCARCE_AVAILABILITY,
    /** Well below their hours target. */
    BELOW_TARGET,
    /** Rule 4: continues a run of working days. */
    EXTENDS_RUN,
    /** Rule 3: had been off for a long time. */
    AFTER_LONG_BREAK,
    /** It is in the slot they preferred that day. */
    PREFERRED_SLOT
}

/** SH-90: who proposed an assignment. */
enum class AssignmentSource {
    /** The rules engine ([AutoScheduler]). */
    RULES,
    /** The AI suggestion, checked against every hard rule. */
    AI
}

/** SH-90: why an outside proposal was not used. */
enum class DropReason {
    /** It names a shift, or a person alias, that was not in the request. */
    UNKNOWN_ALIAS,
    /** The shift is not one of the open shifts being planned. */
    SHIFT_NOT_OPEN,
    /** The person is not on the team being planned. */
    UNKNOWN_PERSON,
    /** A second proposal for a shift that already has one. */
    DUPLICATE,
    /** It breaks a hard rule ([DroppedProposal.rule]). */
    BREAKS_RULE
}

/** SH-90: a proposal that was dropped, with why. [shiftId]/[userId] are null when they were unknown. */
data class DroppedProposal(
    val shiftId: String?,
    val userId: String?,
    val reason: DropReason,
    /** The hard rule, for [DropReason.BREAKS_RULE]. */
    val rule: ScheduleReason? = null,
    /** The personal rule, for [ScheduleReason.PERSONAL_CONSTRAINT]. */
    val constraint: StaffConstraint? = null
)

data class PlannedAssignment(
    val shiftId: String,
    val date: String,
    val plannedTime: TimeRange,
    val hours: Double,
    val userId: String,
    val displayName: String,
    /** Who had the shift when planning started (null for an open shift; set when replanning). */
    val previousUserId: String?,
    val reasons: List<AssignmentReason>,
    /** For [AssignmentReason.BALANCED_HOURS]: whom the balancing took it from. */
    val balancedFromUserId: String? = null,
    /** SH-90: the AI proposed it (and it passed every hard rule), or the rules chose it. */
    val source: AssignmentSource = AssignmentSource.RULES
)

data class CandidateRejection(
    val userId: String,
    val displayName: String,
    val reason: ScheduleReason,
    /** The personal rule, for [ScheduleReason.PERSONAL_CONSTRAINT]. */
    val constraint: StaffConstraint? = null
)

/** A shift the plan leaves empty, with why nobody could take it. */
data class UnfilledShift(
    val shiftId: String,
    val date: String,
    val plannedTime: TimeRange,
    val requiredRole: String?,
    /** The most common reason among the team (ties: the most specific one, later in [ScheduleReason]). */
    val reason: ScheduleReason,
    /** Every person and why they could not take it. */
    val rejections: List<CandidateRejection>,
    /** Who had it before replanning; the apply step clears it. */
    val previousUserId: String? = null
) {
    /**
     * The person who came closest to taking it: the one stopped by the latest check in
     * [ScheduleReason]'s order (ties: the first user id). Where [reason] counts the whole team
     * (managers with another role, people who sent no availability), this names what would have to
     * change for the shift to be filled (SH-53: "Jan: contracted hours reached").
     */
    val closest: CandidateRejection?
        get() = rejections.sortedBy { it.userId }.maxByOrNull { it.reason.ordinal }
}

data class StaffSummary(
    val userId: String,
    val displayName: String,
    val contract: SystemContractType,
    val isOwner: Boolean,
    val leadsShifts: Boolean,
    val needsSupervision: Boolean,
    val target: HoursTarget,
    /** Hours this month here before planning (kept assignments). */
    val hoursBefore: Double,
    /** Hours this month here with the plan. */
    val hoursAfter: Double,
    /** Shifts the plan gives them. */
    val newShifts: Int,
    /** Days they work here this month with the plan. */
    val daysWorked: Int,
    /** The longest run of consecutive working days around the range (other months and workplaces included). */
    val longestStreak: Int,
    /** Rule 2: hours they could work in the range (best shift on each day they are available). */
    val availableHours: Double,
    /** Rule 2: their place in the order (0 = served first). */
    val priorityRank: Int,
    /** hoursAfter / target, null when the target is unknown. */
    val targetRatio: Double?
)

data class BalancingMove(val shiftId: String, val fromUserId: String, val toUserId: String, val hours: Double)

data class BalancingSummary(
    val moves: List<BalancingMove>,
    val iterations: Int,
    /** Highest minus lowest hours/target ratio among civil-law contracts, before and after. */
    val spreadBefore: Double,
    val spreadAfter: Double,
    /** The ratios ended within the tolerance. */
    val converged: Boolean
)

enum class WarningKind {
    /** These people gave no availability: with rule 1 they are not scheduled. */
    NO_AVAILABILITY_SUBMITTED,
    /**
     * SH-221: the workplace's first roster, so these people, who gave no availability, were
     * treated as available on every day ([ScheduleOptions.firstRoster]).
     */
    FIRST_ROSTER_WITHOUT_AVAILABILITY,
    /** Pay terms could not be read: their targets are unknown (no cap, no balancing). */
    PAY_TERMS_UNREADABLE,
    /** Their shifts at other workplaces could not be read: overlaps there are not seen. */
    OTHER_WORKPLACES_UNREADABLE,
    /** The range was cut to the roster's month. */
    RANGE_CLAMPED,
    /** Nobody can lead a shift, so blocks of two or more shifts cannot be filled. */
    NO_SHIFT_LEADS,
    /** Assignments kept from before already break a hard rule (the plan does not change them). */
    KEPT_ASSIGNMENT_BREAKS_RULE,
    /**
     * SH-133: nobody who leads could work these days ([ScheduleWarning.shiftIds]: the shifts
     * planned on them), so they were filled without a lead: the roster warns "no shift lead".
     */
    DAYS_WITHOUT_LEAD
}

data class ScheduleWarning(
    val kind: WarningKind,
    val userIds: List<String> = emptyList(),
    val shiftIds: List<String> = emptyList(),
    val reason: ScheduleReason? = null
)

/**
 * The preview: what the plan would do, why, and what it could not do. Nothing is written until
 * ApplyAutoScheduleUseCase applies it.
 */
data class SchedulePlan(
    val rosterId: String,
    val range: ScheduleRange,
    val options: ScheduleOptions,
    /** New assignments (and, when replanning, changed ones). */
    val assignments: List<PlannedAssignment>,
    val unfilled: List<UnfilledShift>,
    val staff: List<StaffSummary>,
    /** Rule 2's order: user ids, served first to last. */
    val priorityOrder: List<String>,
    val balancing: BalancingSummary,
    val warnings: List<ScheduleWarning>,
    /** Shifts the plan had to fill. */
    val shiftsToFill: Int,
    /** SH-90: outside proposals ([ScheduleProblem.proposals]) that were not used, with why. */
    val dropped: List<DroppedProposal> = emptyList()
) {
    val filled: Int get() = shiftsToFill - unfilled.size
    val fillRate: Double get() = if (shiftsToFill == 0) 1.0 else filled.toDouble() / shiftsToFill
}

/** Minutes of a "H:mm"/"HH:mm" clock time; "24:00" is 1440. */
internal fun clockMinutes(time: String): Int {
    val t = time.trim()
    val colon = t.indexOf(':')
    if (colon < 0) return t.toInt() * 60
    return t.substring(0, colon).toInt() * 60 + t.substring(colon + 1, minOf(t.length, colon + 3)).toInt()
}
